/**
 * parseToolProgressFrame — shared SSE-frame validator for the W16 /
 * TIME-198 `tool_progress` channel.
 *
 * Both `useChatV2` (chat V2) and `ChatInterface` (legacy V1) consume
 * the same tool_progress wire shape. Extracting the validator keeps
 * the closed-enum check (status, layer) in one place — version skew
 * between V1 and V2 was the W16 smoke-test bug we hit. A shared,
 * unit-tested parser closes that drift.
 *
 * Returns the typed `ToolProgressData` on a valid frame, `null`
 * otherwise. Caller drives state on the result.
 */

import type { ToolProgressData } from './toolProgressTypes'

const VALID_STATUSES = new Set([
  'invoked',
  'binding_resolved',
  'fetching',
  'completed',
  'failed',
])

const VALID_LAYERS = new Set(['snapshot', 'eventhouse', 'eventstream'])

/** Best-effort parse of an arbitrary parsed SSE-frame object into a
 *  `ToolProgressData`. Returns null when the frame isn't tool_progress
 *  *or* fails closed-enum validation — defense-in-depth so a malformed
 *  wire payload (or version skew) can't push a bogus state into React.
 */
export function parseToolProgressFrame(parsed: unknown): ToolProgressData | null {
  if (!parsed || typeof parsed !== 'object') return null
  const f = parsed as {
    type?: unknown
    tool?: unknown
    status?: unknown
    detail?: unknown
  }
  if (f.type !== 'tool_progress') return null
  if (typeof f.tool !== 'string' || f.tool.length === 0) return null
  if (typeof f.status !== 'string' || !VALID_STATUSES.has(f.status)) return null

  const out: ToolProgressData = {
    tool: f.tool,
    status: f.status as ToolProgressData['status'],
  }

  const detail = f.detail
  if (detail && typeof detail === 'object') {
    const d = detail as { layer?: unknown; node_path?: unknown }
    const layerOk = typeof d.layer === 'string' && VALID_LAYERS.has(d.layer)
    const nodePathOk = typeof d.node_path === 'string' && d.node_path.length > 0
    if (layerOk || nodePathOk) {
      out.detail = {
        ...(layerOk ? { layer: d.layer as 'snapshot' | 'eventhouse' | 'eventstream' } : {}),
        ...(nodePathOk ? { node_path: d.node_path as string } : {}),
      }
    }
  }

  return out
}
